/**
 * 脚本资产编排（脚本节点开发方案 §5.1 useScriptAssets / §8-1 / §8-3）：
 * - 资产提取：entities 由 strategy 在生成时一次性解析（后端结构化返回优先），
 *   extractAssetsFromShots 按「台词说话人 + 场景/道具后缀词」做前端关键词降级兜底，
 *   仅在 assets 为空且 shots 非空时由面板兜底提取一次，不随面板编辑重提（§8-3 补充定稿）；
 * - 资产生成编排（v1.4.56 画布节点承载）：generateOne 不再面板内静默轮询，改为创建/复用
 *   IMAGE_GENERATOR 生成节点（prompt 注入全局风格 + 面板所选模型/画质/分辨率/比例），
 *   其右连接点连线脚本节点左连接点后经 onNodeAction 入全局串行队列——画布扫光（charge-*）
 *   与排队态开箱即用，关闭面板生成不中断；
 * - 生成进度与终态由 useScriptAssetGenSync（EditorLayout 挂载，面板关闭仍存活）按耗时估算
 *   回写节点 progress 与 script.assets.progress，SUCCESS/ERROR/取消分别写回 imageUrl/FAILED/
 *   PENDING；资产删除时级联清理孤儿生成节点，资产 WORKING 但节点失活时兜底回 PENDING；
 * - 画质档经 scriptAssetRef.quality 由 imageGeneratorStrategy 透传 params.quality（画布常规
 *   生图无画质概念，仅资产节点携带）；编排层 upstreamTexts 与角色参考图收集跳过 scriptAssetRef
 *   节点，防资产生图提示词污染下游分镜/生图语义。
 */
import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { AppNode, NodeStatus, NodeType } from '../../types';
import { useNodeStore } from '../../stores/nodeStore';
import {
  SCRIPT_ASSET_KIND_LABELS,
  ScriptAsset,
  ScriptAssetGenRef,
  Shot,
  createEmptyAsset,
  AssetGenOptions,
} from '../../types/scriptNode';

/** 场景后缀词（关键词兜底提取用，覆盖常见短剧场景） */
const SCENE_SUFFIXES = [
  '会议室', '办公室', '卧室', '客厅', '厨房', '卫生间', '书房', '天台', '车库',
  '教室', '操场', '图书馆', '体育馆', '宿舍',
  '地铁站', '火车站', '车站', '机场', '码头',
  '咖啡馆', '餐厅', '酒吧', '便利店', '超市', '商场', '酒店', '医院', '药店',
  '街道', '马路', '小巷', '巷子', '桥上', '路口',
  '公园', '森林', '海边', '山顶', '山谷', '田野', '湖边', '河边', '沙漠', '废墟',
  '公司', '工厂', '车间', '仓库', '学校', '校园', '寺庙', '城堡', '墓地',
  '房间', '大厅', '走廊', '楼梯间', '电梯里', '车里',
];

/** 道具后缀词（关键词兜底提取用） */
const PROP_SUFFIXES = [
  '手机', '电脑', '平板', '钥匙', '背包', '手提包', '钱包', '雨伞', '书本', '课本',
  '信封', '合同', '文件', '照片', '相册', '日记本', '地图',
  '杯子', '水杯', '咖啡杯', '酒瓶', '酒杯', '烟盒', '打火机',
  '戒指', '项链', '手表', '耳环', '手链',
  '相机', '摄像机', '麦克风', '吉他', '小提琴', '钢琴',
  '花瓶', '台灯', '镜子', '椅子', '行李箱', '纸箱', '礼物盒',
  '水果刀', '菜刀', '手枪', '玩具', '娃娃', '气球', '蜡烛', '药瓶', '口罩',
];

/** 排除的伪说话人（对白「角色名：台词」启发式的噪音过滤） */
const EXCLUDED_SPEAKERS = new Set(['画外音', '旁白', '众人', '大家', '合', '齐', '内心', '独白', 'os', 'OS']);

// 生成节点布局参数（画布像素，对齐 useScriptBatchGenerator 先例：竖排堆叠 + 整组垂直居中）
const ASSET_NODE_EDGE = 420;
const ASSET_GAP_X = 200;
const ASSET_GAP_Y = 60;
const ASSET_PER_COLUMN = 4;
/** 资产图节点行高估算（16:10 比例出图 + 卡片 UI 的实测均值兜底） */
const ASSET_ROW_HEIGHT = 480;
const SCRIPT_HEIGHT_FALLBACK = 420;

/** 读取节点携带的资产生成引用（data 为联合类型，统一宽松断言） */
function readAssetRef(node: AppNode): ScriptAssetGenRef | undefined {
  return (node.data as { scriptAssetRef?: ScriptAssetGenRef }).scriptAssetRef;
}

/** 资产生成提示词组装（与 v1.4.35 全局风格注入一致，画布节点与面板提示共用） */
function buildAssetPrompt(asset: ScriptAsset, kindLabel: string, globalStyle: string): string {
  return [
    `为${kindLabel}「${asset.name}」生成一张设定参考图。`,
    asset.description ? `设定描述：${asset.description}。` : '',
    globalStyle ? `全局画面风格：${globalStyle}。` : '',
    `要求：画面只呈现该${kindLabel}本体，构图完整居中，光线清晰，细节丰富，适合作为后续分镜生图的参考素材。`,
  ].filter(Boolean).join('');
}

/** 查找资产的生成节点（scriptAssetRef 双键定位，一资产至多一节点，重新生成复用） */
function findAssetGenNode(nodes: AppNode[], scriptNodeId: string, assetId: string): AppNode | undefined {
  return nodes.find((n) => {
    const ref = readAssetRef(n);
    return ref?.scriptNodeId === scriptNodeId && ref.assetId === assetId;
  });
}

/** 模块级资产写回（updateScript 纯 store 更新不入撤销栈，进度/终态回写不污染面板撤销历史） */
function patchScriptAsset(scriptNodeId: string, assetId: string, patch: Partial<ScriptAsset>): void {
  useNodeStore.getState().updateScript(scriptNodeId, (script) => ({
    ...script,
    assets: script.assets.map((a) => (a.id === assetId ? { ...a, ...patch } : a)),
  }));
}

/**
 * 前端关键词降级兜底提取（§8-3）：从镜头台词说话人识别角色、
 * 按后缀词识别场景/道具。精度有限，仅后端 entities 缺失时兜底一次。
 */
export function extractAssetsFromShots(shots: Shot[]): ScriptAsset[] {
  const now = Date.now();
  const characters = new Map<string, string>();
  const scenes = new Map<string, string>();
  const props = new Map<string, string>();

  for (const shot of shots) {
    // 角色：剧本对白惯用「角色名：台词」格式，取冒号前称呼
    const speaker = shot.dialogue.match(/^\s*([\u4e00-\u9fa5A-Za-z0-9·]{1,8})\s*[：:]/);
    if (speaker && !EXCLUDED_SPEAKERS.has(speaker[1]) && !characters.has(speaker[1])) {
      characters.set(speaker[1], '从镜头对白中识别的角色');
    }
    const text = `${shot.description} ${shot.dialogue}`;

    // 场景/道具：后缀词匹配（取「前缀+后缀」完整名词）
    for (const suffix of SCENE_SUFFIXES) {
      const m = text.match(new RegExp(`[\\u4e00-\\u9fa5A-Za-z0-9]{0,4}${suffix}`));
      if (m && !scenes.has(m[0])) scenes.set(m[0], '从镜头画面描述中识别的场景');
    }
    for (const suffix of PROP_SUFFIXES) {
      const m = text.match(new RegExp(`[\\u4e00-\\u9fa5A-Za-z0-9]{0,4}${suffix}`));
      if (m && !props.has(m[0])) props.set(m[0], '从镜头画面描述中识别的道具');
    }
  }

  // 子串去重：长名词包含短名词时丢弃短者（「会议室」优先于「议室/室」类短命中）
  const dedupe = (entries: [string, string][]) =>
    entries
      .sort((a, b) => b[0].length - a[0].length)
      .filter(([name], i, arr) => arr.every(([other], j) => j >= i || !other.includes(name)));

  const build = (entries: [string, string][], kind: ScriptAsset['kind']) =>
    dedupe(entries).map(([name, description], i) => ({
      ...createEmptyAsset(kind, name),
      id: `asset-${kind.toLowerCase()}-${now}-${i}`,
      description,
    }));

  return [
    ...build(Array.from(characters.entries()), 'CHARACTER'),
    ...build(Array.from(scenes.entries()), 'SCENE'),
    ...build(Array.from(props.entries()), 'PROP'),
  ];
}

/**
 * 全局资产生成同步 hook（v1.4.56，EditorLayout 挂载，面板关闭仍存活）：
 * 每 2s 扫描 scriptAssetRef 节点——WORKING 按耗时估算进度（60s 线性至 95% 封顶）回写
 * 节点与资产；WORKING/QUEUED → 终态迁移时写回 script.assets（SUCCESS 收图 / ERROR 失败 /
 * IDLE 画布取消回 PENDING）；资产已删的孤儿节点级联清理；资产 WORKING 但节点失活
 * （刷新恢复失败）兜底回 PENDING。
 */
export function useScriptAssetGenSync() {
  useEffect(() => {
    const prevStatus = new Map<string, NodeStatus>();
    const tick = () => {
      const store = useNodeStore.getState();
      for (const node of store.nodes) {
        const ref = readAssetRef(node);
        if (!ref) {
          prevStatus.delete(node.id);
          continue;
        }
        const prev = prevStatus.get(node.id);
        prevStatus.set(node.id, node.status);
        const inFlight = prev === NodeStatus.WORKING || prev === NodeStatus.QUEUED;

        if (node.status === NodeStatus.WORKING) {
          const elapsed = (Date.now() - ref.startedAt) / 1000;
          const pct = Math.min(95, Math.floor((elapsed / 60) * 95));
          const text = `生成中 ${pct}%...`;
          if ((node.data as { progress?: string }).progress !== text) {
            store.updateNode(node.id, { progress: text });
            patchScriptAsset(ref.scriptNodeId, ref.assetId, { progress: text });
          }
          continue;
        }
        if (!inFlight) continue;
        // 在途 → 终态迁移写回（画布取消 / 失败 / 完成）
        if (node.status === NodeStatus.SUCCESS) {
          const image = (node.data as { image?: string }).image;
          patchScriptAsset(ref.scriptNodeId, ref.assetId, {
            ...(image ? { imageUrl: image } : {}),
            status: 'SUCCESS',
            progress: undefined,
          });
        } else if (node.status === NodeStatus.ERROR) {
          patchScriptAsset(ref.scriptNodeId, ref.assetId, { status: 'FAILED', progress: undefined });
        } else if (node.status === NodeStatus.IDLE) {
          patchScriptAsset(ref.scriptNodeId, ref.assetId, { status: 'PENDING', progress: undefined });
        }
      }

      // 一致性兜底：资产标 WORKING 但生成节点缺失/失活（刷新恢复失败）→ 回 PENDING 可再生成
      const liveRefs = new Set<string>();
      for (const node of store.nodes) {
        const ref = readAssetRef(node);
        if (ref && (node.status === NodeStatus.WORKING || node.status === NodeStatus.QUEUED)) {
          liveRefs.add(`${ref.scriptNodeId}:${ref.assetId}`);
        }
      }
      for (const sn of store.nodes) {
        if (sn.type !== NodeType.SCRIPT_NODE) continue;
        const script = (sn.data as { script?: { assets: ScriptAsset[] } }).script;
        if (!script) continue;
        for (const a of script.assets) {
          if (a.status === 'WORKING' && !liveRefs.has(`${sn.id}:${a.id}`)) {
            patchScriptAsset(sn.id, a.id, { status: 'PENDING', progress: undefined });
          }
        }
      }

      // 资产已被删除的孤儿生成节点级联清理（保持「生成节点 ↔ script.assets」一一对应）
      const orphans = store.nodes
        .filter((n) => {
          const ref = readAssetRef(n);
          if (!ref) return false;
          const scriptNode = store.nodes.find((x) => x.id === ref.scriptNodeId);
          const script = scriptNode ? (scriptNode.data as { script?: { assets: ScriptAsset[] } }).script : undefined;
          return !script || !script.assets.some((a) => a.id === ref.assetId);
        })
        .map((n) => n.id);
      if (orphans.length > 0) store.deleteNodes(orphans);
    };
    const timer = window.setInterval(tick, 2000);
    return () => window.clearInterval(timer);
  }, []);
}

/** onAddNode 缺省兜底：直接组装节点入 store（对齐 handleAddNode 的默认值，不记主画布撤销历史） */
function fallbackAddNode(
  type: NodeType,
  position?: { x: number; y: number },
  initialData?: Record<string, unknown>,
): string {
  const defaults: Record<string, unknown> =
    type === NodeType.IMAGE_GENERATOR ? { model: 'grsai-gpt-image-2', aspectRatio: '16:9' } : {};
  const node: AppNode = {
    id: `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    type,
    x: position?.x ?? 100,
    y: position?.y ?? 100,
    width: ASSET_NODE_EDGE,
    title: '',
    status: NodeStatus.IDLE,
    data: { ...defaults, ...initialData } as AppNode['data'],
    inputs: [],
  };
  useNodeStore.getState().addNode(node);
  return node.id;
}

/** 批量编排依赖（与 useScriptBatchGenerator.ScriptBatchOps 同构） */
export interface ScriptAssetOps {
  onNodeAction: (id: string, prompt?: string) => void;
  onAddNode?: (
    type: NodeType,
    position?: { x: number; y: number },
    initialData?: Record<string, unknown>,
  ) => string | undefined;
}

/**
 * 资产生成编排 hook（v1.4.56 画布节点承载）：generateOne 单个 / generateAll 一键批量。
 * 两者均为同步建节点入队（生成进度经 useScriptAssetGenSync 回写，不再面板内轮询）。
 */
export function useScriptAssets(nodeId: string, { onNodeAction, onAddNode }: ScriptAssetOps) {
  const updateScript = useNodeStore((s) => s.updateScript);

  const patchAsset = useCallback((assetId: string, patch: Partial<ScriptAsset>) => {
    updateScript(nodeId, (script) => ({
      ...script,
      assets: script.assets.map((a) => (a.id === assetId ? { ...a, ...patch } : a)),
    }));
  }, [nodeId, updateScript]);

  /**
   * 生成单个资产图：创建/复用 IMAGE_GENERATOR 生成节点并入串行队列，返回是否成功发起。
   * v1.4.56 面板所选参数写入节点（model/aspectRatio/resolution 直落 data，quality 经
   * scriptAssetRef 由策略层透传 params.quality）；重生成复用旧节点重跑（旧图保留作扫光底图，
   * 新图就绪后覆盖——首版重生成即全新 txt2img，与画布生图节点行为一致）。
   */
  const generateOne = useCallback((asset: ScriptAsset, options?: AssetGenOptions): boolean => {
    const store = useNodeStore.getState();
    const scriptNode = store.nodes.find((n) => n.id === nodeId);
    if (!scriptNode) return false;

    const kindLabel = SCRIPT_ASSET_KIND_LABELS[asset.kind];
    // v1.4.35 注入全局视觉风格（script.style），保证角色/场景/道具资产图画风与全片统一
    const globalStyle = ((scriptNode.data as { script?: { style?: string } }).script?.style || '').trim();
    const prompt = buildAssetPrompt(asset, kindLabel, globalStyle);

    const existing = findAssetGenNode(store.nodes, nodeId, asset.id);
    if (existing && (existing.status === NodeStatus.WORKING || existing.status === NodeStatus.QUEUED)) {
      toast.info(`「${asset.name}」正在生成中`);
      return false;
    }

    patchAsset(asset.id, { status: 'WORKING', progress: undefined });
    const ref: ScriptAssetGenRef = {
      scriptNodeId: nodeId,
      assetId: asset.id,
      kind: asset.kind,
      name: asset.name,
      ...(options?.quality ? { quality: options.quality } : {}),
      startedAt: Date.now(),
    };

    if (existing) {
      // 复用旧节点重跑：重置提示词与状态（scriptAssetRef.startedAt 刷新，进度从零估算）
      store.updateNode(existing.id, {
        prompt,
        error: undefined,
        progress: undefined,
        taskId: undefined,
        pendingTaskId: undefined,
        scriptAssetRef: ref,
      }, undefined, undefined, NodeStatus.IDLE);
      onNodeAction(existing.id);
      return true;
    }

    // 新建生成节点：脚本节点左侧竖排堆叠（满 ASSET_PER_COLUMN 向左开新列），整组垂直居中于脚本节点
    const addNodeAt = onAddNode ?? fallbackAddNode;
    const siblings = store.nodes.filter((n) => readAssetRef(n)?.scriptNodeId === nodeId);
    const col = Math.floor(siblings.length / ASSET_PER_COLUMN);
    const row = siblings.length % ASSET_PER_COLUMN;
    const scriptHeight = scriptNode.height ?? SCRIPT_HEIGHT_FALLBACK;
    const groupTop = scriptNode.y + scriptHeight / 2
      - (Math.min(siblings.length + 1, ASSET_PER_COLUMN) * (ASSET_ROW_HEIGHT + ASSET_GAP_Y) - ASSET_GAP_Y) / 2;
    const newId = addNodeAt(
      NodeType.IMAGE_GENERATOR,
      {
        x: scriptNode.x - (ASSET_NODE_EDGE + ASSET_GAP_X) - col * (ASSET_NODE_EDGE + ASSET_GAP_X),
        y: groupTop + row * (ASSET_ROW_HEIGHT + ASSET_GAP_Y),
      },
      {
        prompt,
        ...(options?.modelId ? { model: options.modelId } : {}),
        ...(options?.aspectRatio ? { aspectRatio: options.aspectRatio } : {}),
        ...(options?.resolution ? { resolution: options.resolution } : {}),
        scriptAssetRef: ref,
      },
    );
    if (!newId) {
      patchAsset(asset.id, { status: 'PENDING' });
      return false;
    }
    // 反向连线：资产节点右连接点 → 脚本节点左连接点（target 为脚本节点不触发 IMG2IMG 翻转；
    // scriptAssetRef 上游由编排层跳过文本/参考图收集，不影响分镜生成语义）
    const next = useNodeStore.getState();
    next.addConnection(newId, nodeId);
    next.updateNode(newId, {}, undefined, `${kindLabel}·${asset.name}`);
    onNodeAction(newId);
    return true;
  }, [nodeId, patchAsset, onNodeAction, onAddNode]);

  /** 一键批量生成（v1.4.59 GenerateAllModal 确认后调用）：传入所选资产与参数条选项，逐条建节点
   *  入队（orchestrator 串行队列天然排队）；内部过滤保留作安全兜底（确认间隙可能有资产已完成/
   *  在途），返回成功发起数 */
  const generateAll = useCallback((assets: ScriptAsset[], options?: AssetGenOptions): number => {
    const pending = assets.filter((a) => a.status !== 'SUCCESS' && a.status !== 'WORKING');
    if (pending.length === 0) {
      toast.info('所有资产均已生成或正在生成');
      return 0;
    }
    let okCount = 0;
    for (const asset of pending) {
      if (generateOne(asset, options)) okCount += 1;
    }
    if (okCount > 0) toast.success(`已创建 ${okCount} 个资产生成节点，开始逐个生成`);
    return okCount;
  }, [generateOne]);

  return { generateOne, generateAll };
}
